import { useMemo, useState } from 'react';
import { MessageSquareText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { usePerfReviewsQuery, useReviewCyclesQuery } from '@/client/api/hr/perf';
import {
  REVIEW_STATUS_BADGE,
  REVIEW_STATUS_LABEL,
  REVIEW_TYPE_BADGE,
  REVIEW_TYPE_LABEL,
  type PerformanceReview,
  type ReviewStatus,
} from '@/client/types/hr';
import { HrPageShell } from './components/HrPageShell';
import { EmployeeAvatar, EmployeeLink, EmptyState, StatusBadge } from './components/hr-ui';
import { ReviewText, Stars } from './components/ReviewText';
import { formatDay } from './components/leave-format';
import { useEmployees } from './hr-store';

const filterClass = 'h-9 rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

/** Every review, read-only. Reviews are written by their reviewer in My
 *  workplace; HR reads them here and runs the cycle. */
const ReviewsPage = () => {
  const reviewsQ = usePerfReviewsQuery();
  const cycles = useReviewCyclesQuery().data ?? [];
  const employees = useEmployees();
  const [cycleId, setCycleId] = useState('all');
  const [type, setType] = useState<'all' | 'self' | 'manager'>('all');
  const [status, setStatus] = useState<ReviewStatus | 'all'>('all');
  const [reading, setReading] = useState<PerformanceReview | null>(null);

  const reviews = reviewsQ.data ?? [];
  const byId = useMemo(() => new Map(employees.map((e) => [e.id, e])), [employees]);
  const cycleName = (id: string) => cycles.find((c) => c.id === id)?.name ?? '—';
  const filtered = reviews
    .filter((r) => (cycleId === 'all' || r.cycleId === cycleId) && (type === 'all' || r.type === type) && (status === 'all' || r.status === status))
    .sort((a, b) => (b.submittedAt ?? '').localeCompare(a.submittedAt ?? ''));

  const submitted = reviews.filter((r) => r.status === 'submitted' && r.rating);
  const stats = [
    { key: 'total', label: 'Reviews', value: reviews.length },
    { key: 'submitted', label: 'Submitted', value: reviews.filter((r) => r.status === 'submitted').length },
    { key: 'draft', label: 'Still drafts', value: reviews.filter((r) => r.status === 'draft').length },
    {
      key: 'avg',
      label: 'Average manager rating',
      value: (() => {
        const m = submitted.filter((r) => r.type === 'manager');
        return m.length ? (m.reduce((n, r) => n + (r.rating ?? 0), 0) / m.length).toFixed(1) : '—';
      })(),
    },
  ];

  return (
    <HrPageShell
      mock={false}
      title="Reviews"
      description="Every self and manager review. Reviewers write them in My workplace; this page is for reading."
      crumbs={[{ label: 'Human Resource', href: '/admin/human-resource' }, { label: 'Performance' }, { label: 'Reviews' }]}
      icon={MessageSquareText}
      stats={stats}
    >
      <div className="mb-4 flex flex-wrap gap-3">
        <select value={cycleId} onChange={(e) => setCycleId(e.target.value)} className={filterClass}>
          <option value="all">All cycles</option>
          {cycles.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select value={type} onChange={(e) => setType(e.target.value as 'all' | 'self' | 'manager')} className={filterClass}>
          <option value="all">Self and manager</option>
          <option value="self">Self</option>
          <option value="manager">Manager</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value as ReviewStatus | 'all')} className={filterClass}>
          <option value="all">Any status</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
        </select>
      </div>

      {reviewsQ.isError ? (
        <EmptyState label={`Could not load reviews: ${(reviewsQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : reviewsQ.isLoading ? (
        <EmptyState label="Loading reviews…" />
      ) : filtered.length === 0 ? (
        <EmptyState label={reviews.length ? 'No reviews match these filters.' : 'No reviews yet. Activate a review cycle to open them.'} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">About</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Written by</th>
                <th className="px-4 py-3 font-medium">Cycle</th>
                <th className="px-4 py-3 font-medium">Rating</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((r) => (
                <tr key={r.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setReading(r)}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <EmployeeAvatar employee={byId.get(r.employeeId)} size="sm" />
                      <EmployeeLink employee={byId.get(r.employeeId)} />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge label={REVIEW_TYPE_LABEL[r.type]} className={REVIEW_TYPE_BADGE[r.type]} />
                  </td>
                  <td className="px-4 py-3 text-gray-600">{byId.get(r.reviewerId)?.name ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{cycleName(r.cycleId)}</td>
                  <td className="px-4 py-3">{r.status === 'submitted' ? <Stars rating={r.rating} /> : <span className="text-xs text-gray-400">—</span>}</td>
                  <td className="px-4 py-3">
                    <StatusBadge label={REVIEW_STATUS_LABEL[r.status]} className={REVIEW_STATUS_BADGE[r.status]} />
                    {r.submittedAt && <p className="text-[11px] text-gray-400">{formatDay(r.submittedAt)}</p>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!reading} onOpenChange={(o) => !o && setReading(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {reading && `${REVIEW_TYPE_LABEL[reading.type]} review · ${byId.get(reading.employeeId)?.name ?? ''}`}
            </DialogTitle>
          </DialogHeader>
          {reading && (
            <>
              <p className="text-xs text-gray-500">
                {cycleName(reading.cycleId)} · by {byId.get(reading.reviewerId)?.name ?? 'unknown'} · {REVIEW_STATUS_LABEL[reading.status]}
              </p>
              <ReviewText review={reading} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </HrPageShell>
  );
};

export default ReviewsPage;
